/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
  complaintProcedure,
  financialService,
  privacyPolicy,
  productDisclosure,
  tnc
} from '../../utils/templates/footer';
import {
  EU_REGION,
  BSI_TITLE,
  NIUM_FOREX_FOOTER,
  NIUM_FOREX_FAQ_FORM_URL,
  NIUM_FOREX_TERMS_CONDITION_URL,
  HEADER_FOOTER_IGNORED_URLS
} from '../../utils/constants';

import './footer.scss';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { BSI_LOGO_ICON, INSTAREM_WHITE_LOGO_ICON, SBM_LOGO_ICON } from '@Images';
import { selectIsNiumForexUser, selectTransactionReducer } from '@Redux/selectors';

//https://www.instarem.com/info/all-policies
const footerDetails = [
  {
    country: 'AU',
    head: 'Nium Pte Limited (formerly known as Instarem Pty Ltd) is regulated in Australia by ASIC (AFS license no. 464627)',
    options: [financialService, productDisclosure]
  },
  {
    country: 'CA',
    head: 'Nium Canada Corporation (formerly known as Instarem Canada Corporation) (license no. M15669293) is regulated by the Financial Transactions and Reports Analysis Centre of Canada',
    options: [privacyPolicy, tnc]
  },
  {
    country: 'HK',
    head: 'Nium Limited (formerly known as Instarem Limited) (license no. 16/01/01797) is regulated by the Hong Kong Customs and Excise Department​',
    options: [privacyPolicy, tnc]
  },
  {
    country: 'SG',
    head: 'Nium Pte. Ltd. (formerly known as Instarem Pte. Limited) is regulated by the Monetary Authority of Singapore as a major payment institution under license no. PS20200276.',
    options: [privacyPolicy, tnc]
  },
  {
    country: 'MY',
    head: 'Nium Sdn. Bhd. (formerly known as Instarem Malaysia Sdn. Bhd.) registration no. 201701000367 (1214517-X) is regulated in Malaysia by Central Bank of Malaysia under MY license serial number 00222.',
    options: [privacyPolicy, tnc]
  },
  {
    country: 'IN',
    head: 'Nium India Pvt. Ltd. (formerly known as Instarem India Pvt. Ltd.) operates in association with SBM Bank and is regulated by the Reserve Bank of India approval dated 24th April 2020.',
    options: [privacyPolicy, tnc]
  },
  {
    country: 'US',
    head: 'Nium, Inc. (formerly known as Instarem USA corporation), operates in the United States under a program sponsored by Community Federal Savings Bank, to which Nium is a service provider (NMLS ID no. 1528562).',
    options: [privacyPolicy, tnc]
  },
  {
    country: 'GB',
    head: 'Nium Fintech Limited is authorised by the Financial Conduct Authority under the Electronic Money Regulations 2011 for issuing of electronic money and the provision of payment services with FCA registration number 901024.',
    options: [privacyPolicy, tnc, complaintProcedure]
  },
  {
    country: 'EU',
    head: 'UAB Nium EU (formerly know as UAB Instarem EU) (license no. 14) is regulated by Lietuvos Bankas (Bank of Lithuania)',
    options: [privacyPolicy, tnc, complaintProcedure]
  }
];

const Footer = ({ user_session }) => {
  const location = useLocation();
  const [state, setState] = useState({
    country_code: 'SG',
    account_type: null,
    is_new_user: null,
    noFooter: false
  });
  const transactionDetails = useSelector(selectTransactionReducer)?.transactionDetails;
  const isNiumForexUser = useSelector(selectIsNiumForexUser);

  useEffect(() => {
    if ((window && window.WebViewBridge) || (window && window.ReactNativeWebView)) {
      setState((prevState) => ({
        ...prevState,
        noFooter: true
      }));
    }
  }, []);

  useEffect(() => {
    if (!user_session) return;

    const { country_code, account_type, is_new_user } = user_session;

    setState((prevState) => ({
      ...prevState,
      account_type,
      is_new_user,
      ...(country_code && country_code !== prevState.country_code && { country_code })
    }));
  }, [user_session]);

  const { pathname, state: locationState } = location;
  const { country_code } = state;
  const code = EU_REGION.includes(country_code) && country_code !== 'GB' ? 'EU' : country_code;
  const isTwoFactorToken = locationState && locationState.twoFA ? true : false;

  if (!HEADER_FOOTER_IGNORED_URLS.includes(pathname)) {
    const isFooterDisabled =
      /signin|signup|forgot-password|consent-permission|invite\/|reset-password|logout|feedback\//gi.test(
        pathname
      );
    if (isFooterDisabled) return null;
  }

  const isTxnAckScreen = /acknowledgement\/bank\/success\/*/.test(pathname);
  const isTxnAckScreenMsg =
    'Nium India Pvt. Ltd. (formerly known as Instarem India Pvt. Ltd.) operates in association with SBM Bank and is regulated by the Reserve Bank of India approval dated 24th April 2020.';
  const isSbmReferral = get(transactionDetails, 'additional_info.is_sbm_referral', false);

  const data = footerDetails.find(({ country }) => country === code) || footerDetails[2];
  const footerClass = {};
  const footerImage = BSI_LOGO_ICON;

  const showNewFooter = true;
  footerClass['wrapper'] = `new-footer ${isSbmReferral && isTxnAckScreen && 'new-footer-sbm'}`;
  footerClass['links'] = 'new';

  return (
    !isTwoFactorToken && (
      <div
        className={`detailed-footer ${footerClass.wrapper}`}
        id="detailed-footer"
        style={{ paddingBottom: showNewFooter ? '223px' : '50px' }}
      >
        {isNiumForexUser ? (
          <>
            <div className="nium-forex-wrapper">
              <div>
                <img src={INSTAREM_WHITE_LOGO_ICON} alt="Nium Logo" className="instarem-logo" />
              </div>
              <div>
                <hr className="nium-forex-hr-line" />
              </div>
              <div className="flex-container">
                <div className="column1">
                  <div>{NIUM_FOREX_FOOTER.footerMsg}</div>
                  <div className="certifiedMsg">{NIUM_FOREX_FOOTER.certifiedMsg}</div>
                </div>
                <div className="column2">
                  <img src={footerImage} alt="BSI Logo" title={BSI_TITLE} />
                </div>
              </div>

              <div className="termsAndFaqs">
                <a href={NIUM_FOREX_TERMS_CONDITION_URL} target="_blank" rel="noopener noreferrer">
                  <span>{NIUM_FOREX_FOOTER.termsAndCondition}</span>
                </a>
                <a href={NIUM_FOREX_FAQ_FORM_URL} target="_blank" rel="noopener noreferrer">
                  <span>{NIUM_FOREX_FOOTER.faqs}</span>
                </a>
              </div>
            </div>
          </>
        ) : (
          <>
            <ul>
              {data.options.map(({ text, url }) => (
                <li key={text}>
                  <a
                    className={`know-us-links ${footerClass.links}`}
                    rel="noopener noreferrer"
                    target="_blank"
                    href={url}
                    title={text}
                  >
                    {text}
                  </a>
                </li>
              ))}
            </ul>
            {isSbmReferral ? (
              <p>{isTxnAckScreenMsg}</p>
            ) : (
              <>
                <p>{data.head}</p>
                {country_code === 'IN' && !isSbmReferral && (
                  <div className="logo dcb">
                    <span className="text">In Partnership with</span>
                    <img src={SBM_LOGO_ICON} alt="sbm logo" />
                  </div>
                )}
                <div className="logo sbi">
                  <span className="text">ISO27001 Certified</span>
                  <img src={footerImage} alt="BSI Logo" title={BSI_TITLE} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    )
  );
};

Footer.propTypes = {
  user_session: PropTypes.object
};

export default Footer;
